import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";
import { MapPin, Users, Luggage, Euro, ChevronRight, Bell, BellOff } from "lucide-react";
import { isPushSupported, getPushStatus, subscribePush } from "@/lib/push";

export default function Today() {
  const [data, setData] = useState(null);
  const [driver, setDriver] = useState(null);
  const [pushStatus, setPushStatus] = useState("unknown"); // granted | denied | default | unsupported
  const [pushBusy, setPushBusy] = useState(false);
  const { t, lang } = useLang();

  useEffect(() => {
    api.get("/driver/today").then((r) => setData(r.data));
    api.get("/driver/me").then((r) => setDriver(r.data));
    if (isPushSupported()) getPushStatus().then(setPushStatus);
    else setPushStatus("unsupported");
  }, []);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      await subscribePush();
      setPushStatus("granted");
      toast.success("Notificações activadas ✓");
      // Fire a test push so the driver sees it working immediately
      try { await api.post("/driver/push/test"); } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error(e?.message || "Não foi possível activar");
      const s = await getPushStatus();
      setPushStatus(s);
    } finally {
      setPushBusy(false);
    }
  };

  if (!data) return <div className="p-6 text-slate-500">Loading…</div>;

  const dateFmt = new Date(data.date).toLocaleDateString(lang === "PT" ? "pt-PT" : "en-GB", { weekday: "long", day: "numeric", month: "long" });
  const approvalBanner = driver && driver.status !== "approved" ? (
    <div className={`mb-6 border p-4 ${driver.status === "pending" ? "border-amber-300 bg-amber-50" : "border-red-300 bg-red-50"}`} data-testid="driver-approval-banner">
      <div className="text-[10px] uppercase tracking-widest font-semibold mb-1 text-slate-600">{t("approval_status")}</div>
      <div className={`font-display text-lg ${driver.status === "pending" ? "text-amber-700" : "text-red-700"}`}>
        {driver.status === "pending" && "Awaiting DiTours admin approval — trips will start arriving once approved."}
        {driver.status === "suspended" && "Your account is suspended. Please contact DiTours."}
        {driver.status === "rejected" && "Your application was rejected. Please contact DiTours."}
      </div>
    </div>
  ) : null;

  // Show the "Activar notificações" prompt only if push is supported AND permission not granted yet
  const pushPrompt = (pushStatus === "default") ? (
    <div className="mb-4 border-2 border-[#f59e0b] bg-[#fffaf0] p-4 flex items-start gap-3" data-testid="push-prompt">
      <Bell className="w-5 h-5 text-[#f59e0b] mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <div className="font-display text-base text-[#0a0f1e] mb-1">Activar notificações</div>
        <div className="text-xs text-slate-600 mb-3">Receba pop-up no telemóvel sempre que houver uma viagem nova.</div>
        <button
          onClick={enablePush}
          disabled={pushBusy}
          data-testid="push-enable-btn"
          className="h-9 px-4 bg-[#f59e0b] text-[#0a0f1e] font-semibold uppercase text-[11px] tracking-wide disabled:opacity-50"
        >
          {pushBusy ? "A activar…" : "Activar notificações"}
        </button>
      </div>
    </div>
  ) : (pushStatus === "denied") ? (
    <div className="mb-4 border border-red-300 bg-red-50 p-3 flex items-center gap-2" data-testid="push-denied">
      <BellOff className="w-4 h-4 text-red-600" />
      <span className="text-xs text-red-700">Notificações bloqueadas. Active nas definições do browser para receber viagens em tempo real.</span>
    </div>
  ) : null;

  return (
    <div className="p-5">
      {approvalBanner}
      {pushPrompt}
      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("today")}</p>
      <h1 className="font-display text-3xl text-[#0a0f1e] capitalize">{dateFmt}</h1>
      <p className="text-sm text-slate-500 mt-1">{data.trips.length} {data.trips.length === 1 ? "trip" : "trips"}</p>

      <div className="mt-8 space-y-4">
        {data.trips.length === 0 && (
          <div className="border border-dashed border-slate-200 p-10 text-center text-slate-500 text-sm">
            {t("no_trips_today")}
          </div>
        )}
        {data.trips.map((trip) => {
          const dt = new Date(trip.service_datetime);
          const time = dt.toLocaleTimeString(lang === "PT" ? "pt-PT" : "en-GB", { hour: "2-digit", minute: "2-digit" });
          return (
            <Link key={trip.id} to={`/driver/trip/${trip.id}`} data-testid={`trip-${trip.id}`}
              className="block border border-slate-200 bg-white p-5 hover:border-[#f59e0b] transition-colors">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-4xl text-[#f59e0b] leading-none">{time}</div>
                  <div className="mt-2 inline-block text-[10px] uppercase tracking-wider border border-[#f59e0b] text-[#f59e0b] px-2 py-0.5">
                    {trip.type}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-500" />
              </div>
              <div className="mt-4 text-[#0a0f1e] font-medium">{trip.client_name}</div>
              <div className="text-xs text-slate-500 mt-1">{trip.client_phone}</div>
              <div className="mt-4 space-y-2 text-xs">
                <div className="flex items-start gap-2">
                  <MapPin className="w-3.5 h-3.5 text-[#f59e0b] mt-0.5" />
                  <span className="text-slate-700 flex-1">{trip.pickup_address}</span>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-3.5 h-3.5 text-slate-500 mt-0.5" />
                  <span className="text-slate-500 flex-1">{trip.destination_address}</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {trip.passengers}</span>
                <span className="flex items-center gap-1"><Luggage className="w-3 h-3" /> {trip.luggage}</span>
                <span className="flex items-center gap-1 font-mono text-[#f59e0b]"><Euro className="w-3 h-3" /> {trip.driver_earning?.toFixed(2)}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
